import { Box, Flex, Loading, Text } from "orcs-design-system";

import * as placeholder from "src/components/Placeholder/PlaceholderStyles";

import {
  APP_HEADER_HEIGHT_PX,
  CONTENT_SPINNER_SIZE_PX,
  HEADER_ITEMS,
  SIDEBAR_ICON_SIZE_PX,
  SIDEBAR_ITEM_SIZE_PX,
  SIDEBAR_NAV_ITEM_COUNT,
  SIDEBAR_WIDTH_PX,
} from "./bootShell";

const SidebarNavItemSkeleton = () => (
  <Flex
    alignItems="center"
    justifyContent="center"
    width={SIDEBAR_ITEM_SIZE_PX}
    height={SIDEBAR_ITEM_SIZE_PX}
    flexShrink={0}
    aria-hidden="true"
  >
    <placeholder.Circle
      width={SIDEBAR_ICON_SIZE_PX}
      height={SIDEBAR_ICON_SIZE_PX}
    />
  </Flex>
);

const APP_SHELL_STATUS_MESSAGE_ID = "app-shell-status-message";

export type AppShellSkeletonProps = {
  "data-testid"?: string;
  "aria-label"?: string;
  statusMessage?: string;
};

const AppShellSkeleton = ({
  "data-testid": dataTestId = "app-shell-skeleton",
  "aria-label": ariaLabel = "Loading application",
  statusMessage,
}: AppShellSkeletonProps) => (
  <Flex
    bg="appBackground"
    flexDirection="column"
    height="100vh"
    overflow="hidden"
    data-testid={dataTestId}
    role="status"
    aria-label={statusMessage ? undefined : ariaLabel}
    aria-labelledby={statusMessage ? APP_SHELL_STATUS_MESSAGE_ID : undefined}
    aria-busy="true"
  >
    <Box bg="surface" boxBorder="default" width="100%" aria-hidden="true">
      <Flex
        alignItems="center"
        gap="r"
        px="r"
        height={APP_HEADER_HEIGHT_PX}
        minHeight={APP_HEADER_HEIGHT_PX}
        flexWrap="nowrap"
      >
        {HEADER_ITEMS.map((item, index) => {
          if (item.type === "circle") {
            return (
              <placeholder.Circle
                key={index}
                width={item.size}
                height={item.size}
              />
            );
          }
          if (item.type === "line") {
            return (
              <placeholder.Line
                key={index}
                width={item.width}
                height={item.height}
              />
            );
          }
          return (
            <Box
              key={index}
              flex="1 1 200px"
              minWidth={0}
              maxWidth={550}
              ml="auto"
            >
              <placeholder.Line width={item.width} height={item.height} />
            </Box>
          );
        })}
      </Flex>
    </Box>
    <Flex flex="1" minHeight={0} alignItems="stretch" width="100%">
      <Box
        bg="surface"
        width={SIDEBAR_WIDTH_PX}
        minWidth={SIDEBAR_WIDTH_PX}
        maxWidth={SIDEBAR_WIDTH_PX}
        flexShrink={0}
        borderRight="1px solid"
        borderRightColor="surfaceBorder"
        aria-hidden="true"
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          gap="xs"
          py="s"
          px="xs"
        >
          {Array.from({ length: SIDEBAR_NAV_ITEM_COUNT }, (_, index) => (
            <SidebarNavItemSkeleton key={index} />
          ))}
        </Flex>
      </Box>
      <Flex flex="1" minWidth={0} justifyContent="center" alignItems="center">
        <Flex flexDirection="column" alignItems="center" gap="r">
          <Box color="greyMid" opacity={0.85}>
            <Loading
              variant="teamform"
              teamformAnimation="assemble"
              markSize={CONTENT_SPINNER_SIZE_PX}
              decorative
              data-testid="app-shell-content-spinner"
            />
          </Box>
          {statusMessage ? (
            <Text
              id={APP_SHELL_STATUS_MESSAGE_ID}
              color="subText"
              textAlign="center"
              data-testid="app-shell-status-message"
            >
              {statusMessage}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  </Flex>
);

export default AppShellSkeleton;
